import React, { useEffect, useState } from "react";
import { Calendar, Copy, RefreshCcw, Check, Apple, Globe } from "lucide-react";
import { toast } from "sonner";
import {
  athleteGetCalendarInfo,
  athleteUpdateCalendarSettings,
  athleteRotateCalendarToken,
} from "../../lib/api";

/**
 * CalendarSubscribe — lets the athlete subscribe their training program to
 * Apple Calendar / Google Calendar via an .ics feed URL. The athlete can:
 *   • Pick what time of day workouts appear on their calendar (HH:MM, 24h)
 *   • Copy the subscription URL or use one-tap Apple/Google add buttons
 *   • Rotate the token to invalidate the URL if it was shared by mistake
 *
 * The feed updates as the coach edits the program; calendar apps poll roughly
 * every hour for Google and every 24h for Apple by default — the feed sets
 * `REFRESH-INTERVAL:PT1H` to ask for hourly refresh.
 */
export default function CalendarSubscribe() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [time, setTime] = useState("06:00");
  const [savingTime, setSavingTime] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    athleteGetCalendarInfo()
      .then((i) => {
        setInfo(i);
        setTime(i.calendar_event_time || "06:00");
      })
      .catch(() => setInfo(null))
      .finally(() => setLoading(false));
  }, []);

  const saveTime = async (next) => {
    if (!/^\d{1,2}:\d{2}$/.test(next)) return;
    setSavingTime(true);
    try {
      const r = await athleteUpdateCalendarSettings({
        calendar_event_time: next,
      });
      setTime(r.calendar_event_time);
      toast.success(`Workouts will appear at ${r.calendar_event_time}`);
    } catch (e) {
      toast.error("Couldn't save — use HH:MM 24h format");
    } finally {
      setSavingTime(false);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(info.feed_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy manually");
    }
  };

  const rotateToken = async () => {
    if (
      !window.confirm(
        "Generate a new calendar link? The old link will stop working immediately."
      )
    ) {
      return;
    }
    setRotating(true);
    try {
      const r = await athleteRotateCalendarToken();
      setInfo((prev) => ({
        ...prev,
        feed_url: r.feed_url,
        webcal_url: r.webcal_url,
        google_calendar_add_url: r.feed_url
          ? `https://calendar.google.com/calendar/r?cid=${r.feed_url}`
          : prev.google_calendar_add_url,
      }));
      toast.success("New calendar link generated");
    } catch {
      toast.error("Couldn't rotate link");
    } finally {
      setRotating(false);
    }
  };

  if (loading || !info) return null;

  return (
    <section
      className="bg-white border border-[var(--tir-border)] p-4"
      data-testid="calendar-subscribe-card"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="calendar-subscribe-toggle"
      >
        <span className="inline-flex items-center gap-2">
          <Calendar size={15} className="text-[var(--tir-navy)]" />
          <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
            Sync to your calendar
          </span>
          <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)] normal-case font-normal">
            · workouts at {time}
          </span>
        </span>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-burgundy)] font-semibold">
          {expanded ? "Hide" : "Set up"}
        </span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-5">
          <p className="text-[13px] text-[var(--tir-muted)] leading-relaxed">
            Add your training to Apple Calendar, Google Calendar, or any app
            that supports .ics subscriptions. Your calendar will refresh
            automatically when your coach updates the plan.
          </p>

          {/* Time-of-day picker */}
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
              Workout time
            </label>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              onBlur={(e) => {
                if (e.target.value && e.target.value !== info.calendar_event_time) {
                  saveTime(e.target.value);
                }
              }}
              disabled={savingTime}
              data-testid="calendar-event-time"
              className="font-mono-tir text-sm border border-[var(--tir-border)] hover:border-[var(--tir-ink)] bg-white px-2 py-1"
            />
            <span className="text-[11px] text-[var(--tir-muted)] italic">
              When workouts appear in your day
            </span>
          </div>

          {/* One-tap add buttons */}
          <div className="space-y-2">
            <a
              href={info.webcal_url}
              data-testid="calendar-add-apple"
              className="flex items-center justify-center gap-2 border border-[var(--tir-burgundy)] bg-[var(--tir-burgundy)] text-white hover:bg-[var(--tir-burgundy-dark)] transition-colors px-3 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold"
              title="Adds the subscription to Apple Calendar on macOS / iOS"
            >
              <Apple size={14} /> Add to Apple Calendar (iPhone & Mac)
            </a>
            <div
              className="border border-[var(--tir-burgundy)]/40 bg-[#fdf6f4]"
              data-testid="calendar-google-help"
            >
              <div className="px-3 py-2.5 flex items-start gap-2">
                <Globe size={15} className="text-[var(--tir-navy)] mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)]">
                    Add to Google Calendar
                  </div>
                  <div
                    className="mt-1.5 text-[12px] text-[var(--tir-burgundy)] font-semibold leading-snug"
                    data-testid="google-calendar-warning"
                  >
                    ⚠ Google's mobile apps can't subscribe to external calendars.
                    You must add it from a <strong>desktop browser</strong> first
                    (one time). After that it'll appear on your phone automatically.
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] select-none inline-block">
                      Show desktop instructions
                    </summary>
                    <div className="mt-2 text-[12px] text-[var(--tir-ink)] leading-relaxed space-y-2">
                      <ol className="list-decimal pl-5 space-y-1">
                        <li>
                          On a laptop/desktop browser, open{" "}
                          <a
                            href={info.google_calendar_add_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="calendar-add-google"
                            className="text-[var(--tir-burgundy)] underline font-semibold break-all"
                          >
                            this Google Calendar link
                          </a>
                          .
                        </li>
                        <li>Confirm "Add calendar". You may need to sign into the Google account you use on your phone.</li>
                        <li>
                          <strong>Important — to see it on your phone:</strong>{" "}
                          open{" "}
                          <a
                            href="https://calendar.google.com/calendar/syncselect"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="google-syncselect-link"
                            className="text-[var(--tir-burgundy)] underline font-semibold break-all"
                          >
                            calendar.google.com/calendar/syncselect
                          </a>
                          , check the box next to your coaching program, and
                          tap Save. Subscribed calendars are hidden on the
                          mobile app by default — this is a Google quirk, not
                          something we can fix.
                        </li>
                        <li>Wait 12–24h for the first sync — Google Calendar caches subscriptions slowly. After that, updates appear hourly.</li>
                      </ol>
                      <p className="text-[11px] italic text-[var(--tir-muted)]">
                        Tip: If you're on iPhone, the Apple Calendar button above is much faster and updates every hour.
                      </p>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </div>

          {/* Raw URL + copy + rotate */}
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold mb-1">
              Subscription URL
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={info.feed_url}
                readOnly
                onFocus={(e) => e.target.select()}
                data-testid="calendar-feed-url"
                className="flex-1 min-w-0 font-mono-tir text-[11px] border border-[var(--tir-border)] bg-[var(--tir-ivory)] px-2 py-1.5 text-[var(--tir-muted)]"
              />
              <button
                type="button"
                onClick={copyUrl}
                data-testid="calendar-copy-url-btn"
                className="inline-flex items-center gap-1 border border-[var(--tir-burgundy)] bg-white hover:bg-[var(--tir-burgundy)] hover:text-white transition-colors px-2.5 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-burgundy)]"
              >
                {copied ? <Check size={12} /> : <Copy size={12} />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-[11px] text-[var(--tir-muted)] italic mt-2 leading-relaxed">
              Keep this URL private — anyone with it can read your training.
              If you ever share it by mistake,{" "}
              <button
                type="button"
                onClick={rotateToken}
                disabled={rotating}
                data-testid="calendar-rotate-btn"
                className="underline text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] inline-flex items-center gap-1 font-semibold disabled:opacity-50"
              >
                <RefreshCcw size={10} /> generate a new link
              </button>
              .
            </p>
          </div>

          {/* Provider-specific tips */}
          <details className="border-t border-[var(--tir-border)] pt-3" data-testid="calendar-help-details">
            <summary className="cursor-pointer text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)] inline-block">
              Not working? Step-by-step instructions
            </summary>
            <div className="text-[12px] text-[var(--tir-ink)] mt-3 space-y-3 leading-relaxed">
              <div>
                <span className="font-semibold">Apple Calendar (Mac):</span> File →
                New Calendar Subscription → paste the URL above → choose
                refresh rate "Every hour".
              </div>
              <div>
                <span className="font-semibold">Apple Calendar (iPhone):</span>{" "}
                Tap the Apple Calendar button above — iOS will prompt you to
                add the subscription. If nothing happens, open Settings →
                Calendar → Accounts → Add Account → Other → Add Subscribed
                Calendar → paste the URL.
              </div>
              <div>
                <span className="font-semibold">Google Calendar:</span> Tap the
                Google Calendar button above (web only). Or open Google
                Calendar → "+" next to "Other calendars" → From URL → paste
                the subscription URL.
              </div>
              <div className="text-[11px] italic text-[var(--tir-muted)]">
                Google Calendar can take 12–24h for the first sync. Apple
                refreshes hourly.
              </div>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}
